import React, { useCallback, useEffect, useState } from "react";
import {
  collection,
  doc,
  setDoc,
  getDocs,
  deleteDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../config/firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { hasPermission, PERMISSIONS } from "../utils/roleAccess";

export default function ManageCircular() {
  const [type, setType] = useState("time_table");
  const [selectedClass, setSelectedClass] = useState("");
  const [file, setFile] = useState(null);
  const [circulars, setCirculars] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔐 Auth
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const auth = getAuth();
  const navigate = useNavigate();

  // ================= ADMIN CHECK =================
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        navigate("/");
        return;
      }
      setUser(u);

      const snap = await getDoc(doc(db, "users", u.uid));
      setIsAdmin(
        snap.exists() &&
          hasPermission(snap.data(), PERMISSIONS.MANAGE_CIRCULARS),
      );
      setAuthLoading(false);
    });

    return () => unsub();
  }, [auth, navigate]);

  // ================= CLASSES =================
  const classList = [
    "Nursery",
    "Nursery_A",
    "Nursery_B",
    "LKG",
    "UKG",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
  ];

  // ================= FETCH =================
  const fetchCirculars = useCallback(async () => {
    const snap = await getDocs(collection(db, type));
    const list = snap.docs.map((d) => ({
      id: d.id,
      className:
        d.data().className ||
        d.id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      ...d.data(),
    }));
    setCirculars(list);
  }, [type]);

  // ================= FETCH ON TYPE CHANGE =================
  useEffect(() => {
    if (isAdmin) fetchCirculars();
  }, [isAdmin, fetchCirculars]);

  // ================= UPLOAD =================
  const uploadCircular = async () => {
    if (!selectedClass || !file) {
      alert("Select class & file");
      return;
    }

    try {
      setLoading(true);

      const docId = selectedClass.toLowerCase().replace(/\s+/g, "_");

      const storageRef = ref(storage, `${type}/${docId}/${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await setDoc(doc(db, type, docId), {
        className: selectedClass,
        url,
        uploadedAt: serverTimestamp(),
      });

      setSelectedClass("");
      setFile(null);
      fetchCirculars();
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ================= DELETE =================
  const deleteCircular = async (id, url) => {
    if (!window.confirm("Delete this circular?")) return;

    await deleteObject(ref(storage, url));
    await deleteDoc(doc(db, type, id));
    fetchCirculars();
  };

  // ================= GUARDS =================
  if (authLoading) return <div className="p-10 text-center">Loading...</div>;

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="mb-4 text-gray-600">Unauthorized</p>
        <button
          onClick={() => navigate("/login")}
          className="px-6 py-2 bg-purple-600 text-white rounded"
        >
          Login with an authorized account
        </button>
      </div>
    );
  }

  // ================= UI =================
  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Manage Circulars</h2>

      {/* ===== Tabs ===== */}
      <div className="flex gap-4 mb-6">
        {[
          { key: "time_table", label: "Time Table" },
          { key: "exam_schedule", label: "Exam Schedule" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setType(t.key)}
            className={`px-5 py-2 rounded-full font-medium transition ${
              type === t.key
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ===== Upload Card ===== */}
      <div className="bg-white rounded-xl shadow p-6 mb-8">
        <div className="grid md:grid-cols-3 gap-4">
          <select
            className="border rounded px-3 py-2"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">Select Class</option>
            {classList.map((c) => (
              <option key={c} value={c}>
                {c.replace("_", " ")}
              </option>
            ))}
          </select>

          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => setFile(e.target.files[0])}
          />

          <button
            onClick={uploadCircular}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded px-4"
          >
            {loading
              ? "Uploading..."
              : "Upload " + type.replace("_", " ").toUpperCase()}
          </button>
        </div>
      </div>

      {/* ===== Table ===== */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-blue-50">
            <tr>
              <th className="p-3 text-left">Class</th>
              <th className="p-3 text-left">File</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {circulars.length === 0 ? (
              <tr>
                <td colSpan="3" className="p-6 text-center text-gray-500">
                  No files uploaded
                </td>
              </tr>
            ) : (
              circulars.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="p-3 font-medium">{c.className}</td>
                  <td className="p-3">
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View File
                    </a>
                  </td>
                  <td className="p-3 text-center">
                    <button
                      onClick={() => deleteCircular(c.id, c.url)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
